import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import type { Tables } from "@/integrations/supabase/types";
import { getSafeInternalRedirect } from "@/lib/safeRedirect";
import { normalizeDisplayName } from "@/lib/profileText";
import { loadSupabase } from "@/lib/loadSupabase";
import {
  getSupabaseAuthStorageKey,
  shouldInitializeAuthImmediately,
} from "@/lib/authBootstrap";

type Profile = Tables<"profiles">;
type GeoMinerSupabaseClient = Awaited<ReturnType<typeof loadSupabase>>;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  signUp: (email: string, password: string, displayName?: string, redirectPath?: string) => Promise<{ error: Error | null; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(() => shouldInitializeAuthImmediately());
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const mountedRef = useRef(false);
  const initializationRef = useRef<Promise<GeoMinerSupabaseClient> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const fetchProfile = useCallback(async (authUser: User) => {
    try {
      const supabase = await loadSupabase();
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, display_name, avatar_url, location, role, career_interest, is_profile_public, is_verified_geologist, created_at, updated_at")
        .eq("user_id", authUser.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching profile:", error);
      }
      if (mountedRef.current) {
        setProfile(data ? { ...data, email: authUser.email ?? null } : null);
      }
    } catch (error) {
      console.error("Error in fetchProfile:", error);
    }
  }, []);

  const initializeAuth = useCallback((): Promise<GeoMinerSupabaseClient> => {
    if (initializationRef.current) return initializationRef.current;

    setLoading(true);
    const initialization = (async () => {
      const supabase = await loadSupabase();
      if (!mountedRef.current) return supabase;

      if (!unsubscribeRef.current) {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, nextSession) => {
            if (!mountedRef.current) return;
            if (event === "PASSWORD_RECOVERY") setIsPasswordRecovery(true);
            if (event === "SIGNED_OUT") setIsPasswordRecovery(false);
            setSession(nextSession);
            setUser(nextSession?.user ?? null);

            // Defer profile work out of Supabase's auth callback lock.
            if (nextSession?.user) {
              window.setTimeout(() => {
                if (mountedRef.current) void fetchProfile(nextSession.user);
              }, 0);
            } else {
              setProfile(null);
            }
            setLoading(false);
          },
        );
        unsubscribeRef.current = () => subscription.unsubscribe();
      }

      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!mountedRef.current) return supabase;

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (currentSession?.user) await fetchProfile(currentSession.user);
      else setProfile(null);
      if (mountedRef.current) setLoading(false);
      return supabase;
    })().catch((error) => {
      initializationRef.current = null;
      if (mountedRef.current) {
        console.error("Error initializing authentication:", error);
        setLoading(false);
      }
      throw error;
    });

    initializationRef.current = initialization;
    return initialization;
  }, [fetchProfile]);

  useEffect(() => {
    mountedRef.current = true;

    if (shouldInitializeAuthImmediately()) {
      void initializeAuth().catch(() => undefined);
    } else {
      setLoading(false);
    }

    const authStorageKey = getSupabaseAuthStorageKey();
    const handleStorage = (event: StorageEvent) => {
      if (!authStorageKey || event.key !== authStorageKey) return;
      if (event.newValue && event.newValue !== "null") {
        void initializeAuth().catch(() => undefined);
      } else if (!initializationRef.current) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setIsPasswordRecovery(false);
        setLoading(false);
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("storage", handleStorage);
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      initializationRef.current = null;
    };
  }, [initializeAuth]);

  const signUp = async (email: string, password: string, displayName?: string, redirectPath = "/") => {
    try {
      const supabase = await initializeAuth();
      const safeRedirectPath = getSafeInternalRedirect(redirectPath);
      const redirectUrl = new URL(safeRedirectPath, window.location.origin).toString();
      const safeDisplayName = normalizeDisplayName(displayName);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: safeDisplayName,
          },
        },
      });

      if (error) {
        return { error, needsEmailConfirmation: false };
      }

      return { error: null, needsEmailConfirmation: !data.session };
    } catch (error) {
      return { error: error as Error, needsEmailConfirmation: false };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const supabase = await initializeAuth();
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    const supabase = await initializeAuth();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      isPasswordRecovery,
      signUp,
      signIn,
      signOut,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
